import type { IconType } from "react-icons";
import {
  SiGoogle,
  SiGoogleads,
  SiInstagram,
  SiMeta,
  SiPinterest,
  SiSnapchat,
  SiTiktok,
  SiYoutube,
} from "react-icons/si";

/**
 * A brand mark and colour per traffic source.
 *
 * WHY THIS EXISTS: legacy's table is readable at a glance because every row is anchored by
 * a recognisable logo — a merchant scanning for "how did Meta do" finds the blue infinity
 * before they finish reading the column.
 *
 * WHY react-icons RATHER THAN LEGACY'S FILES. Legacy ships `meta.svg`, `google.svg`,
 * `Chatgpt.png` and friends in its `public/` folder. Copying third-party brand artwork of
 * unknown provenance between repositories is not a refactor decision; `react-icons` is a
 * dependency legacy ALREADY has, its glyphs come from Simple Icons (CC0 artwork), and
 * naming a platform to identify that platform's own data is exactly what the mark is for.
 *
 * THE KEYS ARE `classifySource`'s OUTPUT, not the screenshot's labels — `meta`,
 * `meta-organic`, `google`, `google-organic`, `direct`, and whatever the merchant's own
 * `utm_source` happened to say (rule 4 of the classifier keeps unknown sources verbatim).
 * An unmatched source is NOT an error: it gets a neutral dot, because a merchant using a
 * channel we have never heard of is the normal case, not a bug.
 */

export interface SourceMark {
  readonly Icon: IconType | null;
  /** The brand's own colour, so the mark reads as itself rather than as our theme. */
  readonly color: string;
}

const MARKS = new Map<string, SourceMark>([
  ["meta", { Icon: SiMeta, color: "#0866FF" }],
  ["meta-organic", { Icon: SiInstagram, color: "#E4405F" }],
  ["google", { Icon: SiGoogleads, color: "#4285F4" }],
  ["google-organic", { Icon: SiGoogle, color: "#4285F4" }],
  ["google_display", { Icon: SiGoogleads, color: "#4285F4" }],
  ["youtube", { Icon: SiYoutube, color: "#FF0000" }],
  ["tiktok", { Icon: SiTiktok, color: "#010101" }],
  ["pinterest", { Icon: SiPinterest, color: "#BD081C" }],
  ["snapchat", { Icon: SiSnapchat, color: "#FFFC00" }],
  // NO OpenAI MARK: simple-icons dropped it, and drawing a lookalike to fill the gap
  // would be worse than the neutral dot an unrecognised source already gets.
  ["instagram", { Icon: SiInstagram, color: "#E4405F" }],
]);

/** Neutral, for `direct`, `[Blank]` and every source we do not recognise. */
const UNKNOWN: SourceMark = { Icon: null, color: "var(--muted-foreground)" };

export function sourceMark(source: string | number | null | undefined): SourceMark {
  if (source === null || source === undefined) return UNKNOWN;
  const key = String(source).trim().toLowerCase();
  return MARKS.get(key) ?? UNKNOWN;
}
